/**
 * @fileoverview LocalStorage persistence helpers for React Query
 * Enables instant loading from cache while background refresh happens
 */

const CACHE_PREFIX = 'rq_cache_';
const DEFAULT_MAX_AGE = 60 * 60 * 1000; // 1 hour

interface CachedData<T> {
  data: T;
  timestamp: number;
}

/**
 * Get cached data from localStorage
 */
export function getCachedQueryData<T>(
  key: string,
  maxAge: number = DEFAULT_MAX_AGE
): T | undefined {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + key);
    if (!cached) return undefined;
    
    const parsed: CachedData<T> = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    if (age < maxAge && parsed.data) {
      return parsed.data;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Save data to localStorage cache
 */
export function setCachedQueryData<T>(key: string, data: T): void {
  try {
    const cache: CachedData<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cache));
  } catch {
    // Ignore localStorage errors (quota, etc.)
  }
}

/**
 * Generate a stable cache key from query key array
 */
export function getCacheKey(queryKey: unknown[]): string {
  return queryKey
    .map(k => (k === null || k === undefined ? '' : String(k)))
    .filter(Boolean)
    .join('_');
}

/**
 * Clear old cache entries to prevent localStorage bloat
 * Call periodically or on app start
 */
export function clearStaleCacheEntries(maxAge: number = 24 * 60 * 60 * 1000): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CACHE_PREFIX)) continue;
      
      const cached = localStorage.getItem(key);
      if (!cached) continue;
      
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp > maxAge) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key); // Invalid JSON, remove it
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
}
